import React, { useState, useContext } from 'react';
import { useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import {
  UploadIcon,
  PlusIcon,
  PencilSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import PaginatedGentlyTable, {
  TableColumn,
} from 'src/components/common/PaginatedGentlyTable';
import Page from 'src/components/page/Page';
import MDTypography from 'src/components/md/MDTypography';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardAction,
} from '@/components/ui/card';
import BulkCSVUpload, {
  UploadResult,
} from 'src/components/common/BulkCSVUpload';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import useDivisions from 'src/api/organization/useDivisions';
import useCreateDivision from 'src/api/organization/useCreateDivision';
import useDeleteDivision from 'src/api/organization/useDeleteDivision';
import { DivisionDTO, DivisionWithTagsDTO } from '@erp/shared-types';
import DivisionModal from 'src/components/division/DivisionModal';
import paths from 'src/paths';
import {
  useIsModuleEnabled,
  ProductModule,
} from 'src/hooks/organization-config/useOrganizationEnabledModules';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';

const DivisionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error, refetch } = useDivisions(true);
  const createDivision = useCreateDivision();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [editingDivision, setEditingDivision] = useState<
    DivisionDTO | undefined
  >();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [divisionToDelete, setDivisionToDelete] = useState<DivisionDTO>();
  const deleteDivisionMutation = useDeleteDivision(
    divisionToDelete?.id || '',
    () => {
      refetch();
      setDeleteConfirmOpen(false);
      setDivisionToDelete(undefined);
    },
  );
  const { isModuleEnabled } = useIsModuleEnabled();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);

  // Expected CSV columns for bulk division upload
  const expectedColumns = [
    'name',
    'description',
    'geography',
    'aliases',
    'internal_code',
  ];

  // Handler for delete confirmation
  const handleDeleteDivision = (division: DivisionDTO) => {
    setDivisionToDelete(division);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteDivision = () => {
    if (divisionToDelete) {
      deleteDivisionMutation.mutate();
    }
  };

  const columns: TableColumn<DivisionWithTagsDTO>[] = [
    {
      id: 'name',
      label: 'Division Name',
      sortable: true,
    },
    {
      id: 'internalCode' as keyof DivisionWithTagsDTO,
      label: 'Internal Code',
      sortable: false,
      render: (value: unknown, row: DivisionWithTagsDTO) => {
        const internalCode = (row.metadata as Record<string, unknown>)
          ?.internal_code;
        return internalCode ? String(internalCode) : '-';
      },
    },
    {
      id: 'metadata',
      label: 'Description',
      sortable: false,
      render: (value, row) => (
        <MDTypography variant="body2">
          {(row.metadata?.description as string) || '-'}
        </MDTypography>
      ),
    },
    {
      id: 'metadata',
      label: 'Banner',
      sortable: false,
      render: (value, row) => (
        <MDTypography variant="body2">
          {row.tags?.map((tag: { name: string }) => tag.name).join(', ') || '-'}
        </MDTypography>
      ),
    },

    {
      id: 'metadata',
      label: 'Geography',
      sortable: false,
      render: (value, row) => (
        <MDTypography variant="body2">
          {(row.metadata?.geo as string) || '-'}
        </MDTypography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (value) => (
        <MDTypography variant="body2">
          {new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(value))}
        </MDTypography>
      ),
    },
    {
      id: 'updatedAt',
      label: 'Updated',
      sortable: true,
      render: (value) => (
        <MDTypography variant="body2">
          {new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(new Date(value))}
        </MDTypography>
      ),
    },
    {
      id: 'id',
      label: 'Actions',
      sortable: false,
      minWidth: isAdminUser ? '120px' : '80px',
      render: (value, row) => (
        <div onClick={(e) => e.stopPropagation()} className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              handleEditDivision(row);
            }}
          >
            <PencilSimpleIcon className="h-4 w-4" />
          </Button>
          {isAdminUser && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteDivision(row)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleRowClick = (division: DivisionDTO) => {
    navigate({
      to: paths.authenticated.division,
      params: { divisionId: division.id },
    });
  };

  const handleCreateDivision = () => {
    setEditingDivision(undefined);
    setIsModalOpen(true);
  };

  const handleBulkUpload = () => {
    setIsBulkUploadOpen(true);
  };

  const handleEditDivision = (division: DivisionDTO) => {
    setEditingDivision(division);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingDivision(undefined);
  };

  const handleBulkUploadClose = () => {
    setIsBulkUploadOpen(false);
  };

  const handleModalSuccess = () => {
    refetch();
  };

  // Handle individual row upload for bulk CSV
  const handleUploadRow = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rowData: Record<string, any>,
    index: number,
  ): Promise<void> => {
    try {
      // Transform CSV row data to match API format
      const divisionData = {
        name: rowData.name,
        metadata: {
          description: rowData.description || null,
          geo: rowData.geography || null,
          aliases: rowData.aliases || null,
          internal_code: rowData.internal_code || null,
          importSource: 'csv_bulk_upload',
          csvRowIndex: index,
        },
      };

      // Call the API to create division
      await createDivision.mutateAsync(divisionData);

      console.log(
        `Successfully uploaded division ${index + 1}:`,
        divisionData.name,
      );
    } catch (error) {
      console.error(`Failed to upload division ${index + 1}:`, error);
      throw error; // Re-throw to trigger failed status
    }
  };

  // Handle completion of bulk upload
  const handleBulkUploadComplete = (results: UploadResult[]) => {
    const successful = results.filter((r) => r.status === 'success').length;
    const failed = results.filter((r) => r.status === 'failed').length;

    console.log(
      `Bulk upload completed: ${successful} successful, ${failed} failed`,
    );

    // Refresh the divisions list
    refetch();

    // Close modal only if all uploads succeeded
    if (failed === 0) {
      setIsBulkUploadOpen(false);
    }

    // Individual API calls will show their own success/error toasts via useMutationApi
  };

  const tableActions = (
    <div className="flex gap-2">
      <Button onClick={handleBulkUpload} variant="outline">
        <UploadIcon className="mr-2 h-4 w-4" />
        Bulk Upload
      </Button>
      <Button onClick={handleCreateDivision}>
        <PlusIcon className="mr-2 h-4 w-4" />
        Create Division
      </Button>
    </div>
  );

  // Check if we're on a child route (not the index page itself)
  const isChildRoute = location.pathname !== '/divisions';

  // If we're on a child route, just render the Outlet
  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <>
      <Page isLoading={isLoading} error={error ?? undefined}>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Divisions</CardTitle>
            <CardAction>{tableActions}</CardAction>
          </CardHeader>
          <CardContent>
            <PaginatedGentlyTable<DivisionDTO>
              data={data?.divisions || []}
              columns={columns}
              isLoading={isLoading}
              searchPlaceholder="Search divisions..."
              onRowClick={handleRowClick}
              emptyMessage="No divisions found"
              searchFields={['name']}
              showSearch={true}
              showCount={true}
              paginationConfig={{
                enabled: false,
                pagination: {
                  currentPage: 1,
                  pageSize: 50,
                  hasNextPage: false,
                  hasPreviousPage: false,
                },
                onPageSizeChange: () => {},
                onPreviousPage: () => {},
                onNextPage: () => {},
                onFirstPage: () => {},
                showRowsPerPage: false,
                showPageInfo: false,
              }}
              defaultSort={{
                sortBy: 'name',
                sortDirection: 'asc',
              }}
            />
          </CardContent>
        </Card>
      </Page>

      {/* Single Division Modal */}
      <DivisionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        division={editingDivision}
        onSuccess={handleModalSuccess}
      />

      {/* Bulk Upload Modal */}
      <Dialog
        open={isBulkUploadOpen}
        onClose={handleBulkUploadClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            height: 'auto',
            width: '95vw',
            maxWidth: '95vw',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h4">Bulk Division Upload</MDTypography>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', flex: 1 }}>
          <BulkCSVUpload
            title=""
            description="Upload a CSV file containing division information. Each row will be processed as a new division."
            expectedColumns={expectedColumns}
            onUploadRow={handleUploadRow}
            onComplete={handleBulkUploadComplete}
            maxRows={500}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDivisionToDelete(undefined);
        }}
        onConfirm={confirmDeleteDivision}
        title="Delete Division"
        message={`Are you sure you want to delete "${divisionToDelete?.name}"? This will permanently delete the division and ALL associated data including aliases, rebate rule associations, manual rebatable amounts, and modules. This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteDivisionMutation.isLoading}
        dangerous={true}
      />
    </>
  );
};

export default DivisionsPage;
